import { useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { DueItem, KundeListe, MeinTeam, TicketAngelegt, TicketAnlegen as TicketAnlegenEingabe, TicketKalender, TicketSpalte,
              Unternehmen } from "@kundeninfo/api-client";
import { Arbeitsdialog, Feld, WIEDERVORLAGE_VORGABE_TAGE, darfAbbrechen, vorgabeWiedervorlage } from "@kundeninfo/ui";
import { Auswahl, Auswahlmenue, Chip, Formular, Hinweis } from "@kundeninfo/ui/me-design";
import { personOption } from "./Avatar.js";
import { KiKnopf, Suchfeld } from "../../components/meHuellen.js";
import { hausfarbeChip } from "../../markenfarben.js";
import { alleFeiertage, berlinerTag } from "./ticketFormat.js";
import { DUE_ITEM_TEXT } from "../termine/dueItemTexts.js";
import { SUPPLIER_SOURCES } from "../termine/dueItems.js";
import { HIERARCHY_TEXT } from "./hierarchyTexts.js";
import { ownerFromValue } from "./hierarchy.js";
import { OwnerField } from "./OwnerField.js";
import { SchreibHinweis, useSchreibweg } from "./useSchreibweg.js";
import type { TicketsClient } from "./ticketsClient.js";
import { JEDE_ROLLE } from "../partners/rollenFilter.js";
import { REFERENCE_TEXT } from "./referenceTexts.js";
import type { ReferenceKind } from "./referenceTexts.js";
import "./TicketAnlegen.css";

/**
 * AUFGABE ANLEGEN (Spec „Aufgabe anlegen", F34; seit Paket 1c Entscheid 195): dasselbe Panel rechts
 * wie das Ticket (`Arbeitsdialog panel="ticket"`, gleiche gemerkte Breite), Kreuz, Anlegen/Abbrechen
 * im Fuß; nach dem Anlegen öffnet der Bereich das neue Ticket im Panel. Gestapelte Rückfrage beim
 * Abbrechen mit Eingaben (Hausregel
 * Modal-Bearbeitung). Vorgaben: Zuständig = ich; Spalte = erste offene Spalte rechts des
 * Eingangs, sonst Eingang; Wiedervorlage = `vorgabeWiedervorlage` (EINE Stelle in
 * `@kundeninfo/ui`; seit Entscheid 136 keine Vorgabe: die neue Aufgabe steht ohne Datum oben in
 * „Jetzt“, das Feld sagt das als Hilfe); Unternehmen = Auswahl des Boards, bei „Alle" das
 * Arbeitsunternehmen.
 *
 * Aus der Akte steht der Kunde fest; wählbar sind nur die aktiven Unternehmen, denen er
 * zugeordnet ist (F42, Review-Fokus 4) — bei genau einem ohne Rückfrage, bei mehreren als
 * Auswahl mit dem Arbeitsunternehmen vorn, falls zugeordnet. Die Beschreibung wird
 * Beschreibung des Tickets (F9).
 *
 * Geschrieben wird über `useSchreibweg` (422 am Feld, Rest als Meldung). Der Weg reicht die
 * Antwort des Anlegens (`TicketAngelegt`) weiter; ein Nachlesen des Details gibt es nicht mehr.
 */

/** Die Quellen, aus denen ein Fälligkeitsticket entsteht (Server: `DUE_TICKET_SOURCES`). */
type DueOriginSource = NonNullable<TicketAnlegenEingabe["dueOrigin"]>["source"];

/** Die Felder, an denen ein 422-Befund steht; alle übrigen (etwa `customerIds`) zeigt die Meldung. */
const FELDER = ["title", "descriptionMarkdown", "columnId", "assigneeUserId", "resubmitDate", "teamId",
                "ownerUserId", "ownerTeamId", "dueDate"] as const;

export function TicketAnlegen(p: {
  client: TicketsClient; companies: readonly Unternehmen[]; ich: { id: string; name: string };
  vorgabeCompanyId: string | null; arbeitsunternehmenId: string | null;
  kunde?: { id: string; name: string; kuerzel: readonly string[] };
  /**
   * Ein vorbelegter BEZUG (Lieferanten Aufgabe 22: „Ticket anlegen“ am Liefervertrag bzw. an der Eingangsrechnung, Spec
   * „Glocke“). Er gehört zum Unternehmen des Tickets (Aufgabe 20) — das Unternehmen steht damit fest; nach dem Anlegen
   * wird er verknüpft.
   */
  bezug?: { kind: ReferenceKind; id: string; label: string; companyId: string };
  /**
   * Eine FÄLLIGKEIT als Ursprung (Terminebene 12, D19): „Ticket anlegen“ an einem Fremdeintrag. Das Unternehmen des
   * Eintrags steht fest, der Partner ist der Kunde (fest), Titel und Beschreibung sind nach den Vorlagen des Eintrags
   * vorbelegt, „Fällig bis“ ist sein Tag; gesendet wird `dueOrigin { source, objectId }`. Aus Zahlung oder Kündigungsfrist
   * ist das Team Pflicht und Eigentümer (Ruling 496, siehe `supplierOrigin`).
   */
  dueOrigin?: { item: DueItem };
  /** 409 `TICKET_DUE_ORIGIN_TAKEN` mit Kennung: das vorhandene Ticket der Fälligkeit öffnen. */
  onVorhanden?: (ticketId: string) => void;
  onAbbrechen: () => void; onAngelegt: (id: string) => void;
  /** Meldet, ob Eingaben im Dialog stehen — für den Verlassen-Wächter des Bereichs (Review 17). */
  onUngespeichert?: (ungespeichert: boolean) => void;
}): ReactElement {
  const item = p.dueOrigin?.item;
  const aktiv = p.companies.filter((c) => c.active
    && (p.bezug === undefined || c.id === p.bezug.companyId) && (item === undefined || c.id === item.companyId));
  const erlaubt = p.kunde === undefined ? aktiv : aktiv.filter((c) => p.kunde!.kuerzel.includes(c.shortCode));
  const [start] = useState(() => [p.vorgabeCompanyId, p.arbeitsunternehmenId]
    .find((id) => id !== null && erlaubt.some((c) => c.id === id)) ?? erlaubt[0]?.id ?? "");
  // Der feste Kunde: die Akte oder — aus einer Fälligkeit — ihr Partner (nicht entfernbar, keine Suche). Bei den
  // Lieferantenquellen ist der Partner ein Lieferant, kein Kunde: Das Kundenfeld bleibt leer und bedienbar (Review G3);
  // der Lieferant steht in der Beschreibung.
  const festerKunde = p.kunde ?? (item?.partner == null || (SUPPLIER_SOURCES as readonly string[]).includes(item.source)
    ? undefined : item.partner);
  const startKunden = festerKunde === undefined ? [] : [{ id: festerKunde.id, name: festerKunde.name }];
  // Ruling 496 (Gesamt-Review Server G1): Aus Zahlung oder Kündigungsfrist entsteht das Ticket privat in einem Team, dessen
  // Mitglieder alle die Lieferantensicht haben — das Team ist Pflicht und zugleich Eigentümer (der Server verlangt
  // `ownerTeamId` eines solchen Teams). Wählbar sind nur diese Teams; das erste ist vorbelegt (zählt nicht als Eingabe).
  const supplierOrigin = item !== undefined && (SUPPLIER_SOURCES as readonly string[]).includes(item.source);
  // Vorbelegung aus der Fälligkeit; sie zählt nicht als Eingabe (Abbrechen fragt erst bei einer Änderung).
  const [vorbelegt] = useState(() => (item === undefined ? { titel: "", beschreibung: "", faellig: "" }
    : { titel: DUE_ITEM_TEXT.ticketTitle(item), beschreibung: DUE_ITEM_TEXT.ticketDescription(item, berlinerTag(new Date().toISOString())),
        faellig: item.date }));

  const [companyId, setCompanyId] = useState(start);
  const [titel, setTitel] = useState(vorbelegt.titel);
  const [beschreibung, setBeschreibung] = useState(vorbelegt.beschreibung);
  const [zustaendig, setZustaendig] = useState(p.ich.id);
  // R7, R15: Eigentümer als `user:<id>` | `team:<id>` | "" (System), Vorgabe ich; „Fällig bis“ ohne Vorgabe.
  const [eigentuemer, setEigentuemer] = useState(`user:${p.ich.id}`);
  const [faellig, setFaellig] = useState(vorbelegt.faellig);
  // F134: Team „" = allgemein (Vorgabe); wählbar sind die eigenen aktiven Teams (ADMIN: alle aktiven).
  const [teamId, setTeamId] = useState("");
  const [teams, setTeams] = useState<MeinTeam[]>([]);
  /** Stand von „Meine Teams“: Erst nach dem Laden sagt der Hinweis, dass es kein geeignetes Team gibt; ist es gescheitert,
   *  sagt er das (Re-Review N3). */
  const [teamsLoad, setTeamsLoad] = useState<"pending" | "done" | "failed">("pending");
  /** Das vorbelegte Team (Lieferantenquelle) — die Grundlage für „geändert“. */
  const [presetTeam, setPresetTeam] = useState("");
  const [leute, setLeute] = useState<{ id: string; name: string; email?: string | null }[]>([]);
  const [spalten, setSpalten] = useState<TicketSpalte[]>([]);
  const [spalte, setSpalte] = useState("");
  const [spalteBeruehrt, setSpalteBeruehrt] = useState(false);
  const [datum, setDatum] = useState("");
  const [datumBeruehrt, setDatumBeruehrt] = useState(false);
  const [kunden, setKunden] = useState<{ id: string; name: string }[]>(startKunden);
  const [suche, setSuche] = useState("");
  const [treffer, setTreffer] = useState<KundeListe[]>([]);
  const neueId = useRef<string | null>(null);
  /** Die Kennung des Tickets, das die Fälligkeit schon hat (409): nach dem Schreibweg öffnet der Bereich es. */
  const vorhandenesTicket = useRef<string | null>(null);
  /** Das angelegte Ticket, an dem der vorbelegte Bezug nicht haftete (Fix-Runde 1 zu Aufgabe 22, K1): Der Dialog sagt
   *  es und bietet statt „Anlegen“ das Öffnen an — ein zweiter Klick legte sonst ein zweites Ticket an. Die Felder sind
   *  dann gesperrt (das Ticket steht schon, Final-Fixrunde Teil C) und die Warnung rollt ins Bild. */
  const [withoutReference, setWithoutReference] = useState<string | null>(null);
  const referenceWarning = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (withoutReference !== null) referenceWarning.current?.scrollIntoView?.({ block: "nearest" });
  }, [withoutReference]);
  // `companyId` steht nur dann an einem Feld, wenn die Auswahl zu sehen ist.
  const weg = useSchreibweg<TicketAngelegt>((d) => { neueId.current = d.id; },
    { felder: erlaubt.length > 1 ? [...FELDER, "companyId"] : FELDER });

  useEffect(() => {
    let lebt = true;
    p.client.listMeineTeams().then((r) => {
      if (!lebt) return;
      const usable = r.items.filter((t) => t.active && (!supplierOrigin || t.supplierViewComplete));
      setTeams(usable);
      const first = supplierOrigin ? usable[0]?.id : undefined;
      if (first !== undefined) { setTeamId(first); setPresetTeam(first); setEigentuemer(`team:${first}`); }
      setTeamsLoad("done");
    }).catch(() => { if (lebt) setTeamsLoad("failed"); });
    p.client.listTicketColumns().then((r) => {
      if (!lebt) return;
      setSpalten(r.items);
      const sortiert = [...r.items].sort((a, b) => a.sortIndex - b.sortIndex);
      const eingang = sortiert.find((s) => s.isInbox);
      const rechts = sortiert.find((s) => s.stateKind === "OPEN" && !s.isInbox
        && s.sortIndex > (eingang?.sortIndex ?? Number.NEGATIVE_INFINITY));
      setSpalte((rechts ?? eingang ?? sortiert[0])?.id ?? "");
    }).catch(() => undefined);
    return () => { lebt = false; };
  }, [p.client, supplierOrigin]);

  // Wer zuständig sein kann (212): ohne Team alle aktiven, mit Team nur, wer es sieht. Passt die bisherige Wahl
  // nicht mehr, fällt sie auf „ich“ (wenn dabei), sonst auf „Niemand“ (F134).
  useEffect(() => {
    let lebt = true;
    (teamId === "" ? p.client.listTicketAssignees() : p.client.listTicketAssignees(teamId)).then((r) => {
      if (!lebt) return;
      setLeute(r.items);
      if (teamId !== "") {
        setZustaendig((z) => (z === "" || r.items.some((u) => u.id === z) ? z
          : r.items.some((u) => u.id === p.ich.id) ? p.ich.id : ""));
      }
    }).catch(() => undefined);
    return () => { lebt = false; };
  }, [p.client, teamId, p.ich.id]);

  // Die Vorgabe der Wiedervorlage rechnet mit „heute“ und den Feiertagen des gewählten
  // Unternehmens aus der Board-Antwort (web darf die Domäne nicht importieren). Der Server
  // speichert das Datum wie gesendet. Hat jemand das Datum angefasst, bleibt es. Solange die
  // Vorgabe lädt, ist „Anlegen“ gesperrt — sonst legte ein schneller Klick ohne Wiedervorlage an.
  // Ohne Vorgabe (Entscheid 136) gibt es nichts zu rechnen und kein Board zu laden.
  const [vorgabeLaedt, setVorgabeLaedt] = useState(false);
  useEffect(() => {
    if (WIEDERVORLAGE_VORGABE_TAGE === null || companyId === "" || datumBeruehrt) {
      setVorgabeLaedt(false);
      return undefined;
    }
    let lebt = true;
    setVorgabeLaedt(true);
    p.client.getTicketBoard({ company: companyId }).then((b) => {
      if (!lebt) return;
      const kalender: TicketKalender[] = b.kalender.filter((k) => k.companyId === companyId);
      setDatum(vorgabeWiedervorlage(b.heute, alleFeiertage(kalender)) ?? "");
    }).catch(() => undefined).finally(() => { if (lebt) setVorgabeLaedt(false); });
    return () => { lebt = false; };
  }, [p.client, companyId, datumBeruehrt]);

  const firma = p.companies.find((c) => c.id === companyId);
  const kuerzel = firma?.shortCode ?? "";
  const geaendert = titel.trim() !== vorbelegt.titel.trim() || beschreibung.trim() !== vorbelegt.beschreibung.trim()
    || zustaendig !== p.ich.id
    || companyId !== start || spalteBeruehrt || datumBeruehrt || teamId !== presetTeam
    || eigentuemer !== (presetTeam === "" ? `user:${p.ich.id}` : `team:${presetTeam}`) || faellig !== vorbelegt.faellig
    || kunden.map((k) => k.id).join() !== startKunden.map((k) => k.id).join();
  const onUngespeichert = p.onUngespeichert;
  useEffect(() => { onUngespeichert?.(geaendert); }, [onUngespeichert, geaendert]);

  async function anlegen(): Promise<void> {
    neueId.current = null;
    vorhandenesTicket.current = null;
    let referenceFailed = false;
    await weg.schreiben(async () => {
      const eingabe: TicketAnlegenEingabe = {
        companyId, title: titel, descriptionMarkdown: beschreibung.trim() === "" ? null : beschreibung,
        columnId: spalte, assigneeUserId: zustaendig === "" ? null : zustaendig,
        resubmitDate: datum === "" ? null : datum, customerIds: kunden.map((k) => k.id),
        teamId: teamId === "" ? null : teamId,
        ...ownerFromValue(eigentuemer), dueDate: faellig === "" ? null : faellig,
        ...(item === undefined ? {} : { dueOrigin: { source: item.source as DueOriginSource, objectId: item.objectId } }),
      };
      const neu = await p.client.createTicket(eingabe).catch((e: unknown) => {
        if (item === undefined || !(e instanceof ApiError)) throw e;
        // Die Fälligkeit hat schon ein Ticket (409) bzw. ist weg (404): ein Hinweis, kein „inzwischen geändert“.
        if (e.status === 409 && e.code === "TICKET_DUE_ORIGIN_TAKEN") {
          vorhandenesTicket.current = e.ticketId ?? null;
          throw new ApiError(422, e.code, DUE_ITEM_TEXT.originTaken);
        }
        if (e.status === 404 && e.code === "TICKET_DUE_ORIGIN_NOT_FOUND") throw new ApiError(422, e.code, DUE_ITEM_TEXT.originMissing);
        throw e;
      });
      // Der Bezug nach dem Anlegen; scheitert er, steht das Ticket trotzdem — der Dialog meldet es (unten).
      if (p.bezug !== undefined) {
        await p.client.addTicketReference(neu.id, p.bezug.kind, p.bezug.id).catch(() => { referenceFailed = true; });
      }
      return neu;
    });
    const id = neueId.current;
    if (id === null) {
      if (vorhandenesTicket.current !== null) p.onVorhanden?.(vorhandenesTicket.current);
      return;
    }
    if (referenceFailed) setWithoutReference(id); else p.onAngelegt(id);
  }

  async function abbrechen(): Promise<void> {
    if (weg.laeuft) return;
    // Das Ticket steht schon — nichts geht verloren.
    if (withoutReference !== null) { p.onAbbrechen(); return; }
    if (!(await darfAbbrechen(geaendert))) return;
    p.onAbbrechen();
  }

  async function suchen(text: string): Promise<void> {
    setSuche(text);
    if (text.trim().length < 2) { setTreffer([]); return; }
    try {
      // Über das GEWÄHLTE Unternehmen, nicht das aktive (E363: Tickets gehen über alle Unternehmen).
      const r = await p.client.getCustomers({ search: text, limit: 20, companyIds: [companyId], ...JEDE_ROLLE });
      setTreffer(r.items);
    } catch {
      setTreffer([]);
    }
  }

  /** Das Eigentümer-Team aus der Liste — bleibt wählbar, auch bevor die Kandidaten geladen sind (Lieferantenquelle). */
  const ownerTeam = teams.find((t) => eigentuemer === `team:${t.id}`);
  const anlegenGesperrt = weg.laeuft || vorgabeLaedt || companyId === "" || spalte === "" || (supplierOrigin && teamId === "");
  /** Die Felder: gesperrt, solange geschrieben wird, und sobald das Ticket ohne Bezug steht. */
  const locked = weg.laeuft || withoutReference !== null;
  return (
    <Arbeitsdialog titel="Aufgabe anlegen" panel="ticket" onAbbrechen={() => { void abbrechen(); }}
      abbrechenGesperrt={weg.laeuft}
      fuss={(
        <>
          {withoutReference === null
            ? <KiKnopf art="primaer" deaktiviert={anlegenGesperrt} onClick={() => { void anlegen(); }}>Anlegen</KiKnopf>
            : <KiKnopf art="primaer" onClick={() => p.onAngelegt(withoutReference)}>{REFERENCE_TEXT.openTicket}</KiKnopf>}
          <KiKnopf deaktiviert={weg.laeuft} onClick={() => { void abbrechen(); }}>Abbrechen</KiKnopf>
        </>
      )}>
      <Formular beschriftung="oben" className="ta__formular">
        <Feld id="ta-titel" label="Titel" wert={titel} bearbeitbar pflicht max={200} gesperrt={locked}
          onChange={(w) => { setTitel(w); weg.feldRaeumen("title"); }} fehler={weg.feldFehler.title} />
        {erlaubt.length > 1 ? (
          <Auswahl beschriftung="Unternehmen" wert={companyId} gesperrt={locked}
            {...(weg.feldFehler.companyId !== undefined ? { fehler: weg.feldFehler.companyId } : {})}
            onGeaendert={(e) => { setCompanyId(e.wert); setTreffer([]); if (festerKunde === undefined) setKunden([]); }}>
            {erlaubt.map((c) => <option key={c.id} value={c.id}>{`${c.shortCode} ${c.name}`}</option>)}
          </Auswahl>
        ) : (
          <div className="ta__unternehmen">
            <span className="ta__leise">Unternehmen</span>
            {firma === undefined
              ? <span>Der Kunde ist keinem aktiven Unternehmen zugeordnet.</span>
              : <span><Chip {...hausfarbeChip(p.companies, firma.shortCode)}>{firma.shortCode}</Chip> {firma.name}</span>}
          </div>
        )}
        {teams.length > 0 && (
          <Auswahl beschriftung="Team" wert={teamId} gesperrt={locked}
            {...(weg.feldFehler.teamId !== undefined ? { fehler: weg.feldFehler.teamId } : {})}
            hilfe={supplierOrigin ? DUE_ITEM_TEXT.supplierTeamHelp
              : teamId === "" ? "Allgemein: alle mit Zugang zum Unternehmen sehen das Ticket."
              : `Nur Mitglieder von ${teams.find((t) => t.id === teamId)?.name ?? "diesem Team"} und die Administration sehen das Ticket.`}
            onGeaendert={(e) => {
              setTeamId(e.wert); weg.feldRaeumen("teamId");
              // Lieferantenquelle: das Team ist der Eigentümer. Sonst endete ein Team-Eigentümer, der nicht das neue Team
              // ist, in 422 TICKET_NICHT_IM_TEAM.
              if (supplierOrigin) setEigentuemer(`team:${e.wert}`);
              else if (eigentuemer.startsWith("team:") && eigentuemer !== `team:${e.wert}`) setEigentuemer(`user:${p.ich.id}`);
            }}>
            {!supplierOrigin && <option value="">Allgemein</option>}
            {teams.map((t) => <option key={t.id} value={t.id}>{`${t.shortCode} ${t.name}`}</option>)}
          </Auswahl>
        )}
        {supplierOrigin && teamsLoad !== "pending" && teams.length === 0 && (
          <Hinweis art="warnung">{teamsLoad === "failed" ? DUE_ITEM_TEXT.teamsLoadError : DUE_ITEM_TEXT.supplierTeamMissing}</Hinweis>
        )}
        {/* Sichtbare Beschriftung wie Team und Zuständig (`kompakt={false}`); „ich“ bleibt wählbar, auch bevor die Kandidaten
            geladen sind (`aktuell`) — sonst passte der vorbelegte Wert auf keine Option. */}
        <OwnerField client={p.client} teamId={teamId === "" ? null : teamId} wert={eigentuemer} kompakt={false}
          aktuell={eigentuemer === `user:${p.ich.id}` ? { kind: "USER", user: { ...p.ich, email: null }, team: null }
            : ownerTeam === undefined ? null
            : { kind: "TEAM", user: null, team: { id: ownerTeam.id, shortCode: ownerTeam.shortCode, name: ownerTeam.name } }}
          ichId={p.ich.id} gesperrt={locked || supplierOrigin} {...((weg.feldFehler.ownerUserId ?? weg.feldFehler.ownerTeamId) === undefined ? {}
            : { fehler: (weg.feldFehler.ownerUserId ?? weg.feldFehler.ownerTeamId)! })}
          onGeaendert={(w) => { setEigentuemer(w); weg.feldRaeumen("ownerUserId"); weg.feldRaeumen("ownerTeamId"); }} />
        {/* me-auswahlmenue (Entscheid 53): Avatar je Option; die eigene Person als „ich“ oben. */}
        <Auswahlmenue beschriftung="Zuständig" wert={zustaendig} gesperrt={locked}
          {...(weg.feldFehler.assigneeUserId !== undefined ? { fehler: weg.feldFehler.assigneeUserId } : {})}
          onGeaendert={(e) => setZustaendig(e.wert)}>
          <option value="" data-art="niemand">Niemand</option>
          <option value={p.ich.id} data-art="ich"
            {...personOption(leute.find((u) => u.id === p.ich.id) ?? p.ich)}>{p.ich.name}</option>
          {leute.filter((u) => u.id !== p.ich.id)
            .map((u) => <option key={u.id} value={u.id} {...personOption(u)}>{u.name}</option>)}
        </Auswahlmenue>
        <Auswahl beschriftung="Spalte" wert={spalte} gesperrt={locked}
          {...(weg.feldFehler.columnId !== undefined ? { fehler: weg.feldFehler.columnId } : {})}
          onGeaendert={(e) => { setSpalte(e.wert); setSpalteBeruehrt(true); }}>
          {spalten.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </Auswahl>
        <Feld id="ta-datum" label="Wiedervorlage" typ="date" wert={datum} bearbeitbar gesperrt={locked}
          onChange={(w) => { setDatum(w); setDatumBeruehrt(true); weg.feldRaeumen("resubmitDate"); }}
          fehler={weg.feldFehler.resubmitDate}
          hilfe={datum === "" ? "ohne Wiedervorlage: steht oben in „Jetzt“" : "Wochenende/Feiertag nur Hinweis"} />
        <Feld id="ta-faellig" label={HIERARCHY_TEXT.dueDate} typ="date" wert={faellig} bearbeitbar gesperrt={locked}
          onChange={(w) => { setFaellig(w); weg.feldRaeumen("dueDate"); }} fehler={weg.feldFehler.dueDate} />
        <Feld id="ta-beschreibung" label="Beschreibung" typ="mehrzeilig" wert={beschreibung} bearbeitbar
          gesperrt={locked} max={20000} hilfe="Markdown: **fett**, _kursiv_, Listen"
          onChange={(w) => { setBeschreibung(w); weg.feldRaeumen("descriptionMarkdown"); }}
          fehler={weg.feldFehler.descriptionMarkdown} />
        {/* Kunden wie die übrigen Felder mit der Beschriftung oben; „ohne Kunde (intern)“ ist die
            Hilfe des leeren Felds, kein Text daneben (Sichtprobe 11). */}
        <div className="ta__kunden">
          {festerKunde === undefined ? (
            <Suchfeld name="Kunde suchen" beschriftung="Kunden" platzhalter="Kunde zuordnen" wert={suche}
              gesperrt={locked} hilfe={kunden.length === 0 ? "ohne Kunde (intern)" : undefined}
              onText={(t) => { void suchen(t); }} />
          ) : <span className="ta__leise">Kunden</span>}
          {kunden.length > 0 && (
            <span className="ta__chips">
              {kunden.map((k) => (
                <Chip key={k.id} {...hausfarbeChip(p.companies, kuerzel)}
                  {...(festerKunde === undefined && !locked
                    ? { entfernbar: true, onEntfernt: () => setKunden(kunden.filter((x) => x.id !== k.id)) } : {})}>
                  {k.name}
                </Chip>
              ))}
            </span>
          )}
          {p.bezug !== undefined && (
            <span className="ta__chips" role="group" aria-label={REFERENCE_TEXT.preset}>
              <span className="ta__leise">{REFERENCE_TEXT.preset}</span>
              <Chip>{p.bezug.label}</Chip>
            </span>
          )}
          {festerKunde === undefined && treffer.length > 0 && (
            <span className="ta__chips">
              {treffer.map((k) => (
                <KiKnopf key={k.id} art="leise" onClick={() => {
                  if (!kunden.some((x) => x.id === k.id)) setKunden([...kunden, { id: k.id, name: k.companyName }]);
                  setSuche(""); setTreffer([]);
                }}>{`${k.companyName} zuordnen`}</KiKnopf>
              ))}
            </span>
          )}
        </div>
      </Formular>
      {withoutReference !== null && p.bezug !== undefined && (
        <div ref={referenceWarning}><Hinweis art="warnung">{REFERENCE_TEXT.presetFailed(p.bezug.label)}</Hinweis></div>
      )}
      <SchreibHinweis meldung={weg.meldung} />
    </Arbeitsdialog>
  );
}
